import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import {
  Box, Card, CardContent, TextField, Button, Typography, Alert, CircularProgress, Avatar,
} from '@mui/material';
import { LockOpen as LockIcon } from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';
import { PASSWORD_RULES_TEXT, passwordProblem, apiErrorMessage } from '../passwordPolicy';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000';

/** Latest selectable date of birth — the browser refuses anything after it. */
const TODAY = new Date().toISOString().slice(0, 10);

export default function InviteSetup() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { applySession } = useAuth();
  const token = searchParams.get('token') || '';

  const [info, setInfo] = useState(null);
  const [validating, setValidating] = useState(true);
  const [invalidMsg, setInvalidMsg] = useState('');
  const [fullName, setFullName] = useState('');
  const [dob, setDob] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!token) {
      setValidating(false);
      setInvalidMsg('No invitation token in the link. Open the link from your email.');
      return;
    }
    axios
      .get(`${API_URL}/api/v1/auth/portal-invite/${token}`)
      .then((res) => { setInfo(res.data); setFullName(res.data.full_name || ''); })
      .catch((err) => {
        setInvalidMsg(apiErrorMessage(err, 'Invalid or expired invitation link.'));
      })
      .finally(() => setValidating(false));
  }, [token]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (!fullName.trim()) {
      setError('Please enter your name.');
      return;
    }
    const problem = passwordProblem(password);
    if (problem) {
      setError(problem);
      return;
    }
    if (password !== confirmPassword) {
      setError('Passwords do not match.');
      return;
    }
    // Checked here as well as on the server so the message lands beside the field
    // rather than as a 422 after the round trip.
    if (!dob) {
      setError('Please enter your date of birth.');
      return;
    }
    if (dob > TODAY) {
      setError('Date of birth cannot be in the future.');
      return;
    }
    setLoading(true);
    try {
      const res = await axios.post(`${API_URL}/api/v1/auth/register/portal`, {
        token,
        password,
        password_confirm: confirmPassword,
        full_name: fullName.trim(),
        date_of_birth: dob,
      });
      applySession(res.data.user);
      navigate('/', { replace: true });
    } catch (err) {
      setError(apiErrorMessage(err, 'Could not complete setup.'));
    } finally {
      setLoading(false);
    }
  };

  if (validating) {
    return (
      <Box sx={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (invalidMsg) {
    return (
      <Box sx={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', p: 3 }}>
        <Card sx={{ maxWidth: 420, width: '100%', textAlign: 'center' }}>
          <CardContent sx={{ p: 4 }}>
            <Typography variant="h5" fontWeight={700} gutterBottom color="error">
              Invalid invitation
            </Typography>
            <Typography color="text.secondary" sx={{ mb: 3 }}>{invalidMsg}</Typography>
            <Button variant="outlined" onClick={() => navigate('/login')}>Go to login</Button>
          </CardContent>
        </Card>
      </Box>
    );
  }

  return (
    <Box sx={{
      minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', p: 3,
      bgcolor: 'background.default',
    }}>
      <Card sx={{ maxWidth: 440, width: '100%' }}>
        <CardContent sx={{ p: 4 }}>
          <Box sx={{ textAlign: 'center', mb: 2 }}>
            <Avatar sx={{ width: 56, height: 56, bgcolor: 'primary.main', mx: 'auto', mb: 1 }}>
              <LockIcon />
            </Avatar>
            <Typography variant="h5" fontWeight={700}>
              Set your password
            </Typography>
            {info?.clinic_name && (
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                {info.clinic_name}
              </Typography>
            )}
            <Typography variant="body2" color="text.secondary">
              {info?.email}
            </Typography>
          </Box>

          {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

          <form onSubmit={handleSubmit}>
            <TextField
              fullWidth
              label="Your full name"
              InputLabelProps={{ shrink: true }}
              value={fullName}
              onChange={(e) => setFullName(e.target.value)}
              margin="normal"
              required
              autoComplete="name"
              helperText="This is how colleagues will see you."
            />
            <TextField
              fullWidth
              label="Date of birth"
              type="date"
              InputLabelProps={{ shrink: true }}
              inputProps={{ max: TODAY }}
              value={dob}
              onChange={(e) => setDob(e.target.value)}
              margin="normal"
              required
              helperText="Held on your staff record."
            />
            <TextField
              fullWidth
              label="New password"
              type="password"
              InputLabelProps={{ shrink: true }}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              margin="normal"
              required
              autoComplete="new-password"
              helperText={PASSWORD_RULES_TEXT}
            />
            <TextField
              fullWidth
              label="Confirm password"
              type="password"
              InputLabelProps={{ shrink: true }}
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              margin="normal"
              required
              autoComplete="new-password"
            />
            <Button
              fullWidth
              type="submit"
              variant="contained"
              size="large"
              disabled={loading}
              sx={{ mt: 2 }}
            >
              {loading ? 'Saving…' : 'Continue to ScribeLive'}
            </Button>
          </form>
          <Button fullWidth sx={{ mt: 2 }} onClick={() => navigate('/login')}>
            Back to login
          </Button>
        </CardContent>
      </Card>
    </Box>
  );
}
